import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import getCookies from "../functions/getCookie";
import setCookie from "../functions/setCookie";
import { getApiLink } from "../functions/getApiLink";

// Флаг для запобігання множинним refresh запитам
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor - додає токен до кожного запиту
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getCookies("access_token_ces");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - обробляє 401 помилки
axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Якщо помилка не 401 або це вже повторний запит - пропускаємо
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Якщо це запит на refresh або login - не повторюємо
    if (
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/sign_in")
    ) {
      return Promise.reject(error);
    }

    // Якщо вже йде процес оновлення токена - ставимо запит в чергу
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return axios(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getCookies("refresh_token_ces");

    if (!refreshToken) {
      isRefreshing = false;
      // Немає refresh токена - перенаправляємо на логін
      if (!window.location.href.includes("login") && !window.location.pathname.includes("reset-password")) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(getApiLink("/api/auth/refresh/"), {
        refresh_token: refreshToken,
      });

      const newToken = response.data.access_token;
      setCookie("access_token_ces", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

      processQueue(null, newToken);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);

      // Refresh не вдався - перенаправляємо на логін
      if (!window.location.href.includes("login") && !window.location.pathname.includes("reset-password")) {
        window.location.href = "/login";
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axios;
